/* transporteRebanadas.js -- EL TRANSPORTE GENERICO DE UNA SUBIDA POR REBANADAS.
 *
 * INFRA-3 (#381). Es lo que hasta hoy vivia dentro de `lib/subidasDelBanco.js` (INFRA-1,
 * PR #352/#354): el troceo en rebanadas de 8 MiB, el SHA-256 de cada una, los reintentos
 * por rebanada, el 401 acotado a tres, los tres techos, la espera de red, el refresco de
 * sesion, la reanudacion por `shas` y el `cerrar` con sus esperas. NEUTRO a proposito:
 * no sabe de grupos, de profesores ni de alumnos. Lo parametriza quien llama:
 *
 *   . `r.urlBase`     el prefijo de las rutas (`/groups/{id}/bank/upload`,
 *                     `/documents/upload`);
 *   . `r.prefijo`     el prefijo de las lineas de consola (`[pipe-inst]`, `[alumno]`);
 *   . `r.ponerPct`    a quien avisar del avance (el toast del banco, la cola del Panel);
 *   . `t` / `textoDeFallo` / `idDelToast` los sigue resolviendo el llamador: aqui no hay
 *     un solo texto de pantalla.
 *
 * `subidasDelBanco.js` conserva sus dos entradas publicas, su registro en vuelo, sus
 * toasts y su linea de URL, y DELEGA aqui todo lo demas. `pages/Dashboard.js` usa
 * `subirPorRebanadas`, la orquestacion del alumno (cierre SINCRONO: la respuesta del
 * `cerrar` es la respuesta de hoy de `POST /documents/upload`).
 *
 * REGLAS DE LA CASA: `lib/api` se pide DENTRO de las funciones (no arriba) para no
 * arrastrar `i18n` al grafo de quien importe esto; ningun texto de pantalla nace aqui.
 * Sin acentos en este archivo, a proposito (ASCII).
 */

/** 8 MiB por rebanada (la ultima mas corta): R2 exige partes >= 5 MiB e iguales salvo
 *  la ultima, y cabe de sobra en el corte de 100 s de Cloudflare. */
export const REBANADA_BYTES = 8 * 1024 * 1024;

/* Los tres techos de una rebanada: inactividad (sin latido de progreso un minuto),
 * respuesta (el cuerpo salio entero y el servidor no contesta; por DEBAJO del corte de
 * 100 s de Cloudflare) y rebanada (el ultimo recurso). */
export const TECHO_INACTIVIDAD_MS = 60000;
export const TECHO_RESPUESTA_MS = 90000;
export const TECHO_REBANADA_MS = 900000;
/** Las tres esperas antes de reenviar la misma rebanada. */
export const ESPERAS_REINTENTO_MS = [2000, 5000, 15000];
/** 401 seguidos que se aguantan antes de dar la sesion por terminada. */
export const MAX_401_SEGUIDOS = 3;
/** Se refresca la sesion si el ultimo refresco tiene mas de esto (token de 15 min). */
export const REFRESCO_CADA_MS = 8 * 60 * 1000;
/** Sin red durante esto -> fallo definitivo. */
export const SIN_RED_MAX_MS = 10 * 60 * 1000;
/** El sondeo de un cierre asincrono (el del banco). */
export const POLL_PRIMERA_MS = 1000;
export const POLL_MS = 2000;
export const CIERRE_MAX_MS = 10 * 60 * 1000;
export const RONDAS_DE_CIERRE = 2;

export const TIPO_DOCUMENTO = 'documento';

/* -- LA CONSOLA --------------------------------------------------------------
 * Van a `console`, que es lo que el usuario puede copiar y pegar, y las mismas en la
 * app, donde el visor las recoge. Una consola rota no puede tumbar una subida. */
export const registrar = (nivel, ...args) => {
  try {
    // eslint-disable-next-line no-console
    (console[nivel] || console.log)(...args);
  } catch (e) { /* una consola rota no puede tumbar una subida */ }
};

export function cliente() { return require('./api'); }

export const ahora = () => Date.now();

/** Cuantas rebanadas tiene un archivo de `size` bytes. */
export function rebanadasDe(size) {
  if (!size || size <= 0) return 0;
  return Math.ceil(size / REBANADA_BYTES);
}

// -- Bytes y SHA-256 ---------------------------------------------------------

/** Los bytes de un Blob (una rebanada, nunca el archivo entero). `arrayBuffer()`
 *  donde exista; `FileReader` donde no (WebViews viejos, jsdom). */
export function bytesDe(blob) {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result);
    lector.onerror = () => reject(lector.error || new Error('no se pudo leer la rebanada'));
    lector.readAsArrayBuffer(blob);
  });
}

const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

/** SHA-256 en JS puro (respaldo cuando no hay `crypto.subtle`). Devuelve hex. */
export function sha256Puro(entrada) {
  const bytes = entrada instanceof Uint8Array ? entrada : new Uint8Array(entrada);
  const largo = bytes.length;
  const bitsAltos = Math.floor((largo * 8) / 0x100000000);
  const bitsBajos = (largo * 8) >>> 0;
  const relleno = ((largo + 9 + 63) >> 6) << 6;
  const msg = new Uint8Array(relleno);
  msg.set(bytes);
  msg[largo] = 0x80;
  const vista = new DataView(msg.buffer);
  vista.setUint32(relleno - 8, bitsAltos);
  vista.setUint32(relleno - 4, bitsBajos);
  let h0 = 0x6a09e667; let h1 = 0xbb67ae85; let h2 = 0x3c6ef372; let h3 = 0xa54ff53a;
  let h4 = 0x510e527f; let h5 = 0x9b05688c; let h6 = 0x1f83d9ab; let h7 = 0x5be0cd19;
  const w = new Uint32Array(64);
  for (let i = 0; i < relleno; i += 64) {
    for (let j = 0; j < 16; j += 1) w[j] = vista.getUint32(i + j * 4);
    for (let j = 16; j < 64; j += 1) {
      const a = w[j - 15]; const b = w[j - 2];
      const s0 = ((a >>> 7) | (a << 25)) ^ ((a >>> 18) | (a << 14)) ^ (a >>> 3);
      const s1 = ((b >>> 17) | (b << 15)) ^ ((b >>> 19) | (b << 13)) ^ (b >>> 10);
      w[j] = (w[j - 16] + s0 + w[j - 7] + s1) >>> 0;
    }
    let a = h0; let b = h1; let c = h2; let d = h3; let e = h4; let f = h5; let g = h6; let h = h7;
    for (let j = 0; j < 64; j += 1) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[j] + w[j]) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }
  return [h0, h1, h2, h3, h4, h5, h6, h7].map((x) => x.toString(16).padStart(8, '0')).join('');
}

/** Cual de los dos hashers va a usar este dispositivo (solo se REGISTRA). */
export function nombreDelHasher() {
  const subtle = typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle;
  return subtle && typeof subtle.digest === 'function' ? 'webcrypto' : 'js';
}

/** SHA-256 hex de unos bytes: `crypto.subtle` si existe, si no el respaldo puro. */
export async function sha256Hex(bytes) {
  const subtle = typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle;
  if (subtle && typeof subtle.digest === 'function') {
    try {
      const resumen = await subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(resumen)).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (e) { /* cae al respaldo */ }
  }
  return sha256Puro(bytes);
}

// -- Clasificacion de fallos -------------------------------------------------

export const estadoDe = (err) => err?.response?.status;
export const codigoDe = (err) => err?.response?.data?.error_code;
export const sinRespuesta = (err) => !err?.response;

/** Red, timeout, 5xx o 422 `bank_rebanada_corrupta`: se reintenta la misma rebanada. */
export function esReintentable(err) {
  if (sinRespuesta(err)) return true;
  const status = estadoDe(err);
  if (status >= 500) return true;
  return status === 422 && codigoDe(err) === 'bank_rebanada_corrupta';
}

export const sinRed = () => typeof navigator !== 'undefined' && navigator.onLine === false;

export function dormir(ms) { return new Promise((resolve) => { setTimeout(resolve, ms); }); }

export function pedirResincronia(r) { r.resincronizar = true; }
export function despertar(r) { if (r.despertar) { const d = r.despertar; r.despertar = null; d(); } }

/** Espera a que vuelva la red (o a que el modulo la despierte); 10 min -> falla. */
export async function esperarRed(r) {
  if (!sinRed()) return;
  const desde = ahora();
  while (sinRed()) {
    if (ahora() - desde >= SIN_RED_MAX_MS) {
      throw Object.assign(new Error('sin red'), { code: 'ERR_NETWORK', sinRed: true });
    }
    const restante = SIN_RED_MAX_MS - (ahora() - desde);
    await new Promise((resolve) => {
      const reloj = setTimeout(() => { r.despertar = null; resolve(); }, Math.min(restante, 30000));
      r.despertar = () => { clearTimeout(reloj); resolve(); };
    });
  }
  pedirResincronia(r);
}

// -- La sesion ---------------------------------------------------------------

/** Refresca la sesion y RECUERDA si funciono. */
export async function refrescar(r, refrescarSesion) {
  try {
    r.refrescoOk = (await refrescarSesion()) !== false;
  } catch (e) {
    r.refrescoOk = false;                 // si el refresco falla, la subida sale igual
  }
  r.ultimoRefresco = ahora();
  return r.refrescoOk;
}

export async function refrescarSiToca(r, refrescarSesion) {
  if (r.ultimoRefresco === null || ahora() - r.ultimoRefresco > REFRESCO_CADA_MS) {
    await refrescar(r, refrescarSesion);
  }
}

/** El error que se levanta cuando la sesion se acabo a media subida. No lleva texto. */
export class SesionTerminada extends Error {
  constructor(refrescoOk) {
    super('sesion terminada a media subida');
    this.sesionTerminada = true;
    /** `true` si el ultimo refresco SI funciono. */
    this.refrescoOk = !!refrescoOk;
  }
}

// -- Bytes confirmados -------------------------------------------------------

export function bytesDeRebanada(r, n) {
  return n < r.n ? REBANADA_BYTES : r.size - REBANADA_BYTES * (r.n - 1);
}

export function bytesConfirmados(r) {
  let suma = 0;
  r.recibidas.forEach((n) => { suma += bytesDeRebanada(r, n); });
  return suma;
}

/** El servidor es la verdad: que rebanadas tiene. */
export async function consultar(r, api) {
  const res = await api.get(`${r.urlBase}/${r.subidaId}`);
  const datos = res?.data || {};
  r.recibidas = new Set(datos.recibidas || []);
  return datos;
}

export async function resincronizarSiHaceFalta(r, api) {
  if (!r.resincronizar) return null;
  r.resincronizar = false;
  const datos = await consultar(r, api);
  r.ponerPct(Math.floor((bytesConfirmados(r) * 100) / r.size));
  return datos;
}

/** Los tres techos de una rebanada, montados sobre UN `AbortController`. */
export function vigilar() {
  const control = new AbortController();
  let reloj = null;
  let motivo = null;
  const armar = (ms, porque) => {
    if (reloj) clearTimeout(reloj);
    reloj = setTimeout(() => {
      motivo = porque;
      try { control.abort(); } catch (e) { /* abortar dos veces no rompe nada */ }
    }, ms);
  };
  armar(TECHO_INACTIVIDAD_MS, 'inactividad');
  return {
    signal: control.signal,
    latir(cuerpoCompleto) {
      armar(cuerpoCompleto ? TECHO_RESPUESTA_MS : TECHO_INACTIVIDAD_MS,
            cuerpoCompleto ? 'respuesta' : 'inactividad');
    },
    parar() {
      if (reloj) clearTimeout(reloj);
      reloj = null;
      return motivo;
    },
  };
}

// -- Las rebanadas -----------------------------------------------------------

/** Manda la rebanada `n` con sus reintentos. Deja `r.recibidas` con lo que el
 *  servidor confirmo. Lanza el error definitivo. */
export async function enviarRebanada(r, n, api, refrescarSesion) {
  let fallos = 0;
  let seguidos401 = 0;
  for (;;) {
    await esperarRed(r);
    const datos = await resincronizarSiHaceFalta(r, api);
    if (datos && datos.estado && datos.estado !== 'abierta') return datos;
    if (r.recibidas.has(n)) return null;
    await refrescarSiToca(r, refrescarSesion);
    const desde = (n - 1) * REBANADA_BYTES;
    const hasta = Math.min(n * REBANADA_BYTES, r.size);
    const parte = r.file.slice(desde, hasta);
    const sha = await sha256Hex(new Uint8Array(await bytesDe(parte)));
    const form = new FormData();
    form.append('rebanada', parte, r.nombre);
    form.append('sha256', sha);
    const base = bytesConfirmados(r);
    const tam = hasta - desde;
    const vigia = vigilar();
    const comienzo = ahora();
    try {
      const res = await api.post(`${r.urlBase}/${r.subidaId}/rebanada/${n}`, form, {
        timeout: TECHO_REBANADA_MS,
        signal: vigia.signal,
        onUploadProgress: (e) => {
          vigia.latir(!!(e && e.total && e.loaded >= e.total));
          if (!e || !e.total) return;                     // sin total no hay porcentaje honesto
          const llevados = Math.min(e.loaded, tam);
          // Nunca 100 por lo que el XHR dice haber mandado: el 100 llega cuando el
          // servidor confirmo la ultima rebanada (y al cerrar).
          r.ponerPct(Math.min(99, ((base + llevados) * 100) / r.size));
        },
      });
      vigia.parar();
      seguidos401 = 0;
      const recibidas = res?.data?.recibidas;
      if (Array.isArray(recibidas)) r.recibidas = new Set(recibidas);
      else r.recibidas.add(n);
      r.ponerPct((bytesConfirmados(r) * 100) / r.size);
      registrar('info', `${r.prefijo} rebanada ${n}/${r.n} ok ms=${ahora() - comienzo} bytes=${tam}`);
      return null;
    } catch (err) {
      const porque = vigia.parar();
      const codigo = porque || codigoDe(err) || (sinRespuesta(err) ? 'red' : estadoDe(err));
      if (estadoDe(err) === 401) {
        seguidos401 += 1;
        registrar('warn', `${r.prefijo} rebanada ${n}/${r.n} fallo`,
                  { code: 'sesion', status: 401, intento: seguidos401 });
        if (seguidos401 === 1) continue;   // el interceptor ya refresco: se reenvia
        if (seguidos401 === 2) {
          await refrescar(r, refrescarSesion);   // refresco EXPLICITO y se reenvia
          continue;
        }
        throw new SesionTerminada(r.refrescoOk);
      }
      registrar('warn', `${r.prefijo} rebanada ${n}/${r.n} fallo`,
                { code: codigo, status: estadoDe(err) || null, intento: fallos + 1 });
      if (!esReintentable(err)) throw err;
      if (sinRed()) continue;                              // esperarRed se encarga; no cuenta
      if (fallos >= ESPERAS_REINTENTO_MS.length) throw err;
      await dormir(ESPERAS_REINTENTO_MS[fallos]);
      fallos += 1;
    }
  }
}

/** Manda, en orden, todas las rebanadas que falten. */
export async function enviarFaltantes(r, api, refrescarSesion) {
  for (let n = 1; n <= r.n; n += 1) {
    if (r.recibidas.has(n) && !r.resincronizar) continue;
    const datos = await enviarRebanada(r, n, api, refrescarSesion);
    if (datos) return datos;
  }
  return null;
}

/** `POST cerrar` con las mismas esperas que una rebanada ante fallos de red.
 *  Devuelve la RESPUESTA entera (axios); el banco lee `.data`, el alumno la devuelve tal
 *  cual porque su cierre es sincrono y ESA es la respuesta de la subida. */
export async function cerrarBruto(r, api) {
  let fallos = 0;
  for (;;) {
    await esperarRed(r);
    try {
      return await api.post(`${r.urlBase}/${r.subidaId}/cerrar`, {});
    } catch (err) {
      if (estadoDe(err) === 422 && codigoDe(err) === 'bank_subida_incompleta') throw err;
      if (!esReintentable(err)) throw err;
      if (sinRed()) continue;
      if (fallos >= ESPERAS_REINTENTO_MS.length) throw err;
      await dormir(ESPERAS_REINTENTO_MS[fallos]);
      fallos += 1;
    }
  }
}

export async function cerrar(r, api) {
  const res = await cerrarBruto(r, api);
  return res?.data || {};
}

export const faltantesDe = (err) => {
  const detalle = err?.response?.data?.detail;
  const lista = detalle && typeof detalle === 'object' ? detalle.faltantes : null;
  return Array.isArray(lista) ? lista : [];
};

/** Abandona la subida en el servidor. BEST-EFFORT y sin toast propio. */
export async function abandonar(r, api) {
  if (!r.subidaId) return;
  try {
    await api.post(`${r.urlBase}/${r.subidaId}/abandonar`, {});
  } catch (e) { /* el barrido se encarga; el usuario no tiene que enterarse */ }
}

/** Salta las rebanadas que el servidor ya tiene Y cuyo sha local coincide. Al primer
 *  desajuste devuelve `false` (otro archivo con el mismo nombre y peso). */
export async function reanudar(r, api, shas) {
  let saltadas = 0;
  for (let n = 1; n <= r.n; n += 1) {
    if (!r.recibidas.has(n)) break;
    const declarado = shas[String(n)] || shas[n];
    if (!declarado) break;               // el servidor no lo sabe: se reenvia y ya
    const desde = (n - 1) * REBANADA_BYTES;
    const hasta = Math.min(n * REBANADA_BYTES, r.size);
    const local = await sha256Hex(new Uint8Array(await bytesDe(r.file.slice(desde, hasta))));
    if (local !== declarado) {
      registrar('info', `${r.prefijo} subida reanudada subida=${r.subidaId} saltadas=${saltadas} (desajuste en n=${n})`);
      return false;
    }
    saltadas += 1;
    // El porcentaje avanza SIN SUBIR BYTES: reanudar se ve como reanudar.
    r.ponerPct(Math.floor((bytesConfirmados(r) * 100) / r.size));
  }
  registrar('info', `${r.prefijo} subida reanudada subida=${r.subidaId} saltadas=${saltadas}`);
  return true;
}

/** `POST iniciar`. Deja `r.subidaId` y `r.recibidas`; devuelve `res.data`. */
export async function iniciar(r, api, cuerpo) {
  const ini = await api.post(`${r.urlBase}/iniciar`, cuerpo);
  r.subidaId = ini?.data?.subida_id;
  r.recibidas = new Set(ini?.data?.recibidas || []);
  if (!r.subidaId) throw new Error('iniciar sin subida_id');
  return ini?.data || {};
}

/** El registro minimo que el transporte necesita. `ponerPct` es MONOTONO: el numero
 *  visible solo sube (#353 c); `r.recibidas` es el contador interno y baja cuando toca. */
export function registroBase({ file, urlBase, prefijo, alAvanzar }) {
  const r = {
    file, nombre: file.name, size: file.size, urlBase, prefijo: prefijo || '[transporte]',
    n: 0, subidaId: null, recibidas: new Set(), ultimoRefresco: null,
    resincronizar: false, despertar: null, refrescoOk: true, pct: null,
  };
  r.ponerPct = (pct) => {
    if (pct === null || pct === undefined) return;
    const nuevo = Math.max(0, Math.min(100, Math.floor(pct)));
    if (nuevo <= (r.pct === null ? -1 : r.pct)) return;
    r.pct = nuevo;
    try { if (alAvanzar) alAvanzar(nuevo); } catch (e) { /* un oyente roto no tumba la subida */ }
  };
  return r;
}

/* -- LA SUBIDA DEL ALUMNO, DE PUNTA A PUNTA (cierre SINCRONO) ---------------
 *
 * `iniciar` -> rebanadas -> `cerrar`; la respuesta del `cerrar` es la respuesta de hoy
 * de `POST /documents/upload` (documento, `preview_split` o placeholder de ojos), y sus
 * errores llevan los codigos de hoy, asi que `Dashboard.js` los trata igual. Un fallo
 * definitivo del transporte se lanza como el error de axios que lo causo (sin
 * `response` en red/timeouts): cae en el mismo manejo generico de siempre.
 *
 * Consola: `[alumno] subida abierta subida=... partes=... hasher=...` -
 * `[alumno] rebanada n/total ok ms=... bytes=...` - `[alumno] cierre ok doc=... ms=...` -
 * `console.warn('[alumno] rebanada n/total fallo', {code, status, intento})` -
 * `[alumno] reanudada subida=... recibidas=k/total`. */
export async function subirPorRebanadas({ file, urlBase, prefijo = '[alumno]', iniciarBody = {},
                                          alAvanzar, api: apiDado, refrescarSesion: refrescoDado }) {
  // `lib/api` solo se pide si hace falta: con `api` y `refrescarSesion` dados (las
  // pruebas), este modulo no toca la red ni arrastra `i18n`.
  const cli = (apiDado && refrescoDado) ? null : cliente();
  const api = apiDado || cli.default;
  const refrescarSesion = refrescoDado || cli.refrescarSesion;
  const r = registroBase({ file, urlBase, prefijo, alAvanzar });
  r.n = rebanadasDe(r.size);
  const cuerpo = () => ({ filename: r.nombre, size: r.size, total: r.n, tipo: TIPO_DOCUMENTO, ...iniciarBody });
  let ini = await iniciar(r, api, cuerpo());
  registrar('info', `${prefijo} subida abierta subida=${r.subidaId} partes=${r.n} hasher=${nombreDelHasher()}`);
  try {
    if (ini.reanudada === true) {
      const sirvio = await reanudar(r, api, ini.shas || {});
      registrar('info', `${prefijo} reanudada subida=${r.subidaId} recibidas=${r.recibidas.size}/${r.n}`);
      if (!sirvio) {
        await abandonar(r, api);
        r.subidaId = null;
        ini = await iniciar(r, api, cuerpo());
      }
    }
    if (ini.estado !== 'ensamblando') await enviarFaltantes(r, api, refrescarSesion);
    // El renglon llega a 100 cuando el servidor confirmo la ultima rebanada, y se queda
    // ahi durante el cierre (que es sincrono: unos segundos).
    r.ponerPct(100);
    const comienzo = ahora();
    let res;
    try {
      res = await cerrarBruto(r, api);
    } catch (err) {
      if (!(estadoDe(err) === 422 && codigoDe(err) === 'bank_subida_incompleta')) throw err;
      // Solo las que el servidor dice que faltan, y UN cierre mas.
      faltantesDe(err).forEach((n) => r.recibidas.delete(n));
      await enviarFaltantes(r, api, refrescarSesion);
      res = await cerrarBruto(r, api);
    }
    registrar('info', `${prefijo} cierre ok doc=${res?.data?.id || res?.data?.temp_upload_id || ''} ms=${ahora() - comienzo}`);
    return res;
  } catch (err) {
    registrar('warn', `${prefijo} subida fallida subida=${r.subidaId} code=${codigoDe(err) || estadoDe(err) || 'red'}`);
    if (sinRespuesta(err) || err?.sesionTerminada) await abandonar(r, api);
    throw err;
  }
}
